import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, Power, RefreshCw, X } from 'lucide-react';
import { adminAPI } from '../../services/api';

const SLOT_LABELS = {
  discover_feed_inline: 'Discover feed (in-grid card)',
  product_detail_below_desc: 'Product detail (below description)',
  category_banner: 'Category page (banner above grid)',
  empty_search: 'Empty search results',
  profile_sidebar: 'Profile page',
};

const STATUS_COLORS = {
  active: { bg: 'rgba(16,185,129,0.15)', color: '#0a7a5b' },
  scheduled: { bg: 'rgba(245,158,11,0.15)', color: '#92400e' },
  expired: { bg: 'rgba(156,163,175,0.2)', color: '#525252' },
  inactive: { bg: 'rgba(156,163,175,0.2)', color: '#525252' },
};

const emptyForm = {
  slotId: 'discover_feed_inline',
  title: '',
  description: '',
  imageUrl: '',
  destinationUrl: '',
  ctaLabel: 'Learn more',
  isSponsored: true,
  isActive: false,
  startDate: '',
  endDate: '',
};

const toDateInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
};

const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotFilter, setSlotFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await adminAPI.listPromos(slotFilter ? { slotId: slotFilter } : {});
      setPromos(data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load placements');
    } finally {
      setLoading(false);
    }
  }, [slotFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing('new');
    setForm(emptyForm);
    setError('');
  };

  const openEdit = (promo) => {
    setEditing(promo._id);
    setForm({
      slotId: promo.slotId,
      title: promo.title || '',
      description: promo.description || '',
      imageUrl: promo.imageUrl || '',
      destinationUrl: promo.destinationUrl || '',
      ctaLabel: promo.ctaLabel || 'Learn more',
      isSponsored: !!promo.isSponsored,
      isActive: !!promo.isActive,
      startDate: toDateInput(promo.startDate),
      endDate: toDateInput(promo.endDate),
    });
    setError('');
  };

  const closeForm = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/upload/image`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: fd,
        }
      );
      if (!res.ok) throw new Error('Upload failed');
      const payload = await res.json();
      setForm((prev) => ({ ...prev, imageUrl: payload.data.fullSize }));
    } catch (err) {
      setError('Image upload failed. Try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const payload = {
        ...form,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
      };

      if (editing === 'new') {
        await adminAPI.createPromo(payload);
      } else {
        await adminAPI.updatePromo(editing, payload);
      }
      await load();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save placement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this placement? This cannot be undone.')) return;
    try {
      await adminAPI.deletePromo(id);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleToggle = async (id) => {
    try {
      await adminAPI.togglePromo(id);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    fontSize: '14px',
    background: 'var(--background)',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '6px',
    color: 'var(--text)',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0 }}>Promo Placements</h1>
          <p style={{ margin: '4px 0 0 0', color: 'var(--textSecondary)', fontSize: '14px' }}>
            Manage promotional content shown in marketplace slots.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            value={slotFilter}
            onChange={(e) => setSlotFilter(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: '220px' }}
          >
            <option value="">All slots</option>
            {Object.entries(SLOT_LABELS).map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>
          <button
            onClick={openCreate}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 16px',
              background: 'var(--primary)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Plus size={16} />
            New placement
          </button>
        </div>
      </div>

      {error && !editing && (
        <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <RefreshCw size={32} className="spin" style={{ color: 'var(--primary)' }} />
        </div>
      ) : promos.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--cardBackground)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
        }}>
          <h3 style={{ margin: '0 0 8px 0' }}>No placements yet</h3>
          <p style={{ color: 'var(--textSecondary)', margin: 0 }}>
            Create your first placement to start showing promotional content.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--cardBackground)', borderRadius: '12px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ background: 'var(--secondary)', textAlign: 'left' }}>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600 }}>Title</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600 }}>Slot</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600 }}>Window</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>Impr.</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>Clicks</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>CTR</th>
                <th style={{ padding: '12px', fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promos.map((promo) => {
                const statusStyle = STATUS_COLORS[promo.status] || STATUS_COLORS.inactive;
                const ctr = promo.impressions > 0 ? ((promo.clicks / promo.impressions) * 100).toFixed(2) : '0.00';
                const windowText = promo.startDate || promo.endDate
                  ? `${promo.startDate ? new Date(promo.startDate).toLocaleDateString() : '—'} → ${promo.endDate ? new Date(promo.endDate).toLocaleDateString() : '—'}`
                  : 'Always';
                return (
                  <tr key={promo._id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <div style={{ fontWeight: 600 }}>{promo.title}</div>
                      {promo.isSponsored && (
                        <span style={{ fontSize: '10px', color: 'var(--textSecondary)', fontWeight: 600, letterSpacing: '0.5px' }}>
                          SPONSORED
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--textSecondary)' }}>
                      {SLOT_LABELS[promo.slotId] || promo.slotId}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: '999px',
                        background: statusStyle.bg,
                        color: statusStyle.color,
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {promo.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: 'var(--textSecondary)' }}>{windowText}</td>
                    <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right' }}>{promo.impressions || 0}</td>
                    <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right' }}>{promo.clicks || 0}</td>
                    <td style={{ padding: '12px', fontSize: '13px', textAlign: 'right', fontWeight: 600 }}>{ctr}%</td>
                    <td style={{ padding: '12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => setPreview(promo)}
                        title="Preview"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', marginRight: '4px' }}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleToggle(promo._id)}
                        title={promo.isActive ? 'Deactivate' : 'Activate'}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          marginRight: '4px',
                          color: promo.isActive ? '#0a7a5b' : 'var(--textSecondary)',
                        }}
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => openEdit(promo)}
                        title="Edit"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', marginRight: '4px' }}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(promo._id)}
                        title="Delete"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#dc2626' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit modal */}
      {editing && (
        <div
          onClick={closeForm}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--background)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '640px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>{editing === 'new' ? 'New placement' : 'Edit placement'}</h2>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Slot</label>
                <select
                  value={form.slotId}
                  onChange={(e) => setForm({ ...form, slotId: e.target.value })}
                  style={inputStyle}
                >
                  {Object.entries(SLOT_LABELS).map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  maxLength={100}
                  style={inputStyle}
                  placeholder="e.g., Big sale on strollers"
                />
              </div>

              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  maxLength={300}
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  placeholder="Optional secondary text"
                />
              </div>

              <div>
                <label style={labelStyle}>Image *</label>
                {form.imageUrl && (
                  <img
                    src={form.imageUrl}
                    alt="preview"
                    style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }}
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ display: 'block', marginBottom: '8px' }}
                />
                <input
                  type="text"
                  value={form.imageUrl}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  style={inputStyle}
                  placeholder="or paste an image URL"
                />
              </div>

              <div>
                <label style={labelStyle}>Destination URL *</label>
                <input
                  type="url"
                  value={form.destinationUrl}
                  onChange={(e) => setForm({ ...form, destinationUrl: e.target.value })}
                  style={inputStyle}
                  placeholder="https://..."
                />
              </div>

              <div>
                <label style={labelStyle}>CTA label</label>
                <input
                  type="text"
                  value={form.ctaLabel}
                  onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })}
                  maxLength={30}
                  style={inputStyle}
                  placeholder="Learn more"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Start date (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>End date (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isSponsored}
                    onChange={(e) => setForm({ ...form, isSponsored: e.target.checked })}
                  />
                  Show "Sponsored" label (FTC)
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button
                  onClick={() => setPreview({ ...form, _id: 'preview', impressions: 0, clicks: 0 })}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--cardBackground)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Preview
                </button>
                <button
                  onClick={closeForm}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--cardBackground)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.title || !form.imageUrl || !form.destinationUrl}
                  style={{
                    padding: '10px 16px',
                    background: 'var(--primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: saving || !form.title || !form.imageUrl || !form.destinationUrl ? 0.5 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 2100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--background)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '480px',
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Preview — {SLOT_LABELS[preview.slotId] || preview.slotId}</h3>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              {preview.isSponsored && (
                <span style={{
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  zIndex: 2,
                }}>
                  Sponsored
                </span>
              )}
              <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'var(--cardBackground)',
                border: '1px solid var(--border)',
              }}>
                {preview.imageUrl && (
                  <img src={preview.imageUrl} alt={preview.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                )}
                <div style={{ padding: '16px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>{preview.title}</div>
                  {preview.description && (
                    <div style={{ fontSize: '13px', color: 'var(--textSecondary)', marginBottom: '12px' }}>
                      {preview.description}
                    </div>
                  )}
                  <span style={{
                    display: 'inline-block',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    background: 'var(--primary)',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}>
                    {preview.ctaLabel || 'Learn more'}
                  </span>
                </div>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--textSecondary)', marginTop: '12px', marginBottom: 0 }}>
                Destination: <a href={preview.destinationUrl} target="_blank" rel="noopener noreferrer">{preview.destinationUrl}</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromosTab;
